// Drop-in "Connect Facebook Page" button.
//
// Usage:
//   <ConnectFacebook
//     bridgeId="0f9af3ec-..."
//     onConnected={({ page, page_id }) => refetchBridge()}
//     onError={(msg) => setBanner(msg)}
//   />
//
// Flow:
//   1. Button click POSTs to /api/admin/facebook/connect with the
//      operator's Supabase JWT. Response is { oauth_url } (Meta OAuth
//      dialog URL with HMAC-signed state).
//   2. window.open() launches oauth_url in a popup.
//   3. Meta redirects the popup to /api/admin/facebook/callback, which
//      exchanges the code and renders a tiny page that
//      postMessage()s the outcome back to this window and closes.
//   4. This component listens for the message and calls
//      onConnected / onError / onPickPage.
//
// The onPickPage callback fires when the operator manages multiple
// Facebook Pages — the caller is responsible for rendering a picker
// UI and POSTing the chosen page_id + pick_token to
// /api/admin/facebook/finalize. If not provided, multi-page
// operators get an inline error and must select at Meta level.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

const EXPECTED_ORIGIN =
  (typeof window !== 'undefined' && window.location?.origin) ||
  'https://agencyhub.sixfigureagentcrm.com';

export default function ConnectFacebook({
  bridgeId,
  buttonLabel = 'Connect Facebook Page',
  onConnected,
  onPickPage,
  onError,
  className = '',
}) {
  const [busy, setBusy] = useState(false);
  const popupRef = useRef(null);
  const settledRef = useRef(false);

  useEffect(() => {
    function handler(event) {
      if (event.origin !== EXPECTED_ORIGIN) return;
      const msg = event.data;
      if (!msg || msg.source !== 'sfa_facebook_connect') return;
      settledRef.current = true;
      setBusy(false);
      const p = msg.payload || {};
      if (!p.ok) {
        onError?.(p.error || 'unknown_error');
        return;
      }
      if (p.multi_page) {
        onPickPage
          ? onPickPage({ bridgeId: p.bridge_id, pickToken: p.pick_token, pages: p.pages })
          : onError?.('multi_page_no_handler');
        return;
      }
      onConnected?.({ page: p.page, page_id: p.page_id, bridge_id: p.bridge_id });
    }
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onConnected, onPickPage, onError]);

  const start = useCallback(async () => {
    if (busy || !bridgeId) return;
    setBusy(true);
    settledRef.current = false;
    try {
      const jwt = readPersistedJwtDirect();
      if (!jwt) throw new Error('not_signed_in');

      const res = await fetch('/api/admin/facebook/connect', {
        method: 'POST',
        headers: {
          'authorization':  `Bearer ${jwt}`,
          'content-type':   'application/json',
        },
        body: JSON.stringify({ bridge_id: bridgeId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.oauth_url) {
        throw new Error(body?.error || `connect_failed_${res.status}`);
      }

      popupRef.current = window.open(
        body.oauth_url,
        'sfa_fb_connect',
        'width=640,height=780,noopener=no,noreferrer=no',
      );
      if (!popupRef.current) throw new Error('popup_blocked');

      const closeGuard = setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(closeGuard);
          if (!settledRef.current) {
            setBusy(false);
            onError?.('popup_closed_before_completion');
          }
        }
      }, 400);
    } catch (e) {
      setBusy(false);
      onError?.(String(e?.message || 'connect_failed'));
    }
  }, [bridgeId, busy, onError]);

  return (
    <button
      type="button"
      onClick={start}
      disabled={busy || !bridgeId}
      className={
        className ||
        'inline-flex items-center gap-2 rounded-lg bg-[#1877F2] px-4 py-2 text-sm font-semibold text-white hover:bg-[#166fe0] disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
      }
    >
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.269h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
      </svg>
      {busy ? 'Opening Facebook…' : buttonLabel}
    </button>
  );
}
